import {
  Component, EventEmitter, Input, OnDestroy, OnInit,
  Output
} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NotificationsService, ProjectService} from '@catcore/services';
import {GenericResourceApiService} from '@catfeatures/services/generic-resource-api.service';
import {ParametersService} from '@catmodules/models/parameters/services/parameters.service';
import {ActiveModal} from '@catmodules/widgets/slide-in-modal/utils/modal-ref';
import {StyleType} from '@catui/types';
import {Service, Unit} from 'app/modules/models/units/types';
import {Observable} from 'rxjs/Observable';
import {isNullOrUndefined} from 'util';
import {Project} from "@catcore/types/api-v2";
import {Subscription} from "rxjs";
import {extractDataFromModel} from "app/modules/core/utils";
import {SelectItem} from "primeng/primeng";
import {getDirtyFormValues} from "@catcore/utils/form-utils";

/**
 * Provides a modal form to edit feature or project units. The editable parts
 * include e.g. location address, update group, and parameter values.
 */
@Component({
  selector: 'catapp-modify-unit',
  templateUrl: './modify-unit.component.html',
  styleUrls: ['./modify-unit.component.scss'],
})
export class ModifyUnitComponent implements OnInit, OnDestroy {
  @Output() afterDelete = new EventEmitter();
  @Output() afterUpdate = new EventEmitter<Service>();

  @Input() enableDelete = true;

  @Input()
  get unit(): Unit {
    return this._unit;
  }

  set unit(value: Unit) {
    this._unit = value;
    if (!isNullOrUndefined(this._unit)) {
      this.form.patchValue(this._unit);

      if (this.hasParameters()) {
        const formArray = this.form.controls['parameters'] as FormArray;
        while (formArray.length !== 0) {
          formArray.removeAt(0);
        }
        this._unit._embedded.parameters.forEach((param) => {
          const formControl = this.formBuilder.group({
            key: ['', []],
            value: ['', []],
          });
          formArray.push(formControl);
          formControl.get('key').setValue(param.key);
          formControl.get('value').setValue(param.value);
        });
      }

      if (this.hasPhysicalPartNumbers()) {
        this.form.patchValue({'part_number': this._unit.part_number});
        const items: SelectItem[] = [];
        this._unit.physical_part_numbers.forEach((choice: any) => {
          if (choice) {
            items.push({label: choice, value: choice});
          }
        });
        this.physicalPartNrs = items;
      }
    }
  }

  labels = {
    part_number: 'Partnumber',
    description: 'Description',
    feature: 'Feature',
    update_group: 'Update Group',
    location_address: 'Location Address',
    debug_level: 'Debug Level',
    display_label: 'Display label',
  };

  debugLevelOptions = [
    {value: 0, label: "Emergency (0)"},
    {value: 1, label: "Alert (1)"},
    {value: 2, label: "Critical (2)"},
    {value: 3, label: "Error (3)"},
    {value: 4, label: "Warning (4)"},
    {value: 5, label: "Notice (5)"},
    {value: 6, label: "Informational (6)"},
    {value: 7, label: "Debug (7)"},
  ];

  form: FormGroup;
  styleType = StyleType;
  fileList: any[] = [];
  physicalPartNrs: SelectItem[] = null;

  private _fileListSubscription: Subscription;
  private _project: Project;
  private _projectSubscription;

  private _unit: Unit;

  constructor(private formBuilder: FormBuilder,
              private genericResourceService: GenericResourceApiService,
              private activeModal: ActiveModal,
              private parametersService: ParametersService,
              private notification: NotificationsService,
              private projectService: ProjectService) {
    this.form = this.formBuilder.group({
      id: ['', []],
      location_address: ['', [Validators.min(0)]],
      update_group: ['', [Validators.min(0)]],
      parameters: this.formBuilder.array([]),
      debug_level: ['', [Validators.min(0)]],
      display_label: ['', []],
      part_number: ['', []],
    });
  }

  ngOnInit() {
    this._projectSubscription = this.projectService.getCurrentProject().subscribe((project) => {
      this._project = project;
      const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
      if (isActiveProject) {
        this._fileListSubscription = this.projectService.getUploadFiles(this._project)
          .subscribe((files) => {
            extractDataFromModel(files, 'item', '_embedded').forEach((file) => {
              this.fileList.push({label: file.filename, value: file._links.self.href});
            });
            this.fileList.push({label: 'empty', value: null});
          });
      }
    });
  }

  ngOnDestroy() {
    this._projectSubscription.unsubscribe();
    if (this._fileListSubscription) {
      this._fileListSubscription.unsubscribe();
    }
  }

  get parameterFormArray(): FormArray {
    return this.form.get('parameters') as FormArray;
  }

  get label() {
    // TODO: Where is this method used?
    return this._unit ?
        (this._unit.display_label ? this._unit.display_label : this._unit.part_number)
        : '';
  }

  get dataIsReady() {
    return !isNullOrUndefined(this._unit);
  }

  hasParameters(): boolean {
    return this._unit
      && Object.keys(this._unit).includes('_embedded')
      && Object.keys(this._unit._embedded).includes('parameters');
  }

  updateUnit() {
    const requests = [];

    const projectUnitService = this.genericResourceService.getServiceForResource(this._unit._links.self.href);
    const formValue = getDirtyFormValues(this.form);

    requests.push(projectUnitService.customPUT(formValue));

    if (this.hasParameters()) {
      const formArray = this.form.controls['parameters'] as FormArray;
      for (let i = 0; i < formArray.length; i++) {
        if (formArray.at(i).dirty) {
          const param = this._unit._embedded.parameters[i];
          const newParamValue = formArray.at(i).value.value;
          requests.push(this.parametersService.save(param, newParamValue));
        }
      }
    }

    const subscription = Observable.forkJoin(requests)
      .finally(() => subscription.unsubscribe())
      .subscribe((multiple) => {
        this.afterUpdate.emit(multiple[0] as Service);
        this.activeModal.close();
      })
    ;
  }

  deleteUnit() {
    const deleteUser = confirm('Are you sure you want to delete this unit?');
    if (deleteUser) {
      const projectUnitService = this.genericResourceService.getServiceForResource(this._unit._links.self.href);
      projectUnitService
        .remove()
        .subscribe(() => {
          this.notification.success('Unit deleted', 'The unit was successfully removed.');
          this.afterDelete.emit();
          this.activeModal.close();
        });
    }
  }

  hasPhysicalPartNumbers(): boolean {
    return this._unit
      && Object.keys(this._unit).includes('physical_part_numbers')
      && this._unit.physical_part_numbers.length > 0;
  }

  isProjectUnit(): boolean {
    return this._unit
        && Object.keys(this._unit).includes('_links')
        && this._unit._links.self.href.includes('project-')
  }

}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/models/units/components/modify-unit/modify-unit.component.ts